import httpClient from './httpClient';
import { displayCustomerName } from '../constants/customerType';
import { parseApiError } from '../utils/errorHandler';
import { MAX_DELIVERY_PAGE_SIZE, validateDeliveryDateRange } from '../utils/deliveryDateRange';
import { normalizeDeliveryStateFromApi, toApiDeliveryStates } from '../utils/deliveryState';
import { normalizeAddressName } from '../utils/addressName';

/**
 * Serviço de entregas.
 * Listagem: GET /api/entregas e GET /api/entregas/cobraveis (filtros, ordenação e paginação).
 */

/**
 * Cria uma nova entrega.
 * @param {{ customerId?: number | string, requesterId?: number | string }} body
 * @returns {Promise<{ deliveryId: string }>}
 */
async function createDelivery(body) {
    const payload = {};
    if (body.customerId != null) payload.customerId = Number(body.customerId);
    if (body.requesterId != null && body.requesterId !== '') payload.requesterId = Number(body.requesterId);
    const response = await httpClient.post('/api/entregas', payload);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const deliveryId = location
        ? String(location).split('?')[0].replace(/\/+$/, '').split('/').pop() || ''
        : '';
    return { deliveryId };
}

/**
 * Busca os dados completos de uma entrega.
 * GET /api/entregas/{deliveryId}
 * @param {string|number} deliveryId
 * @param {string|number|null|undefined} [customerId]
 * @returns {Promise<Object>} DeliveryDTO bruto da API
 */
async function getDeliveryById(deliveryId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.get(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    return response.json();
}

/**
 * Cancela a entrega (DELETE no backend).
 * DELETE /api/entregas/{deliveryId}
 * @param {string|number} deliveryId
 * @param {string|number|null|undefined} [customerId]
 * @returns {Promise<void>}
 */
async function cancelDelivery(deliveryId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.delete(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Solicita/agenda a entrega.
 * PATCH /api/entregas/{deliveryId}
 * @param {string|number} deliveryId
 * @param {string|number|null|undefined} [customerId]
 * @returns {Promise<void>}
 */
async function requestDelivery(deliveryId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.patch(path, null);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/** Labels de exibição por value do enum (CREATION, PENDING, IN_TRANSIT, ...) */
const STATUS_LABELS = {
    CREATION: 'Em criação',
    CONFIRMATION: 'Aguardando confirmação',
    PENDING: 'Pendente',
    IN_TRANSIT: 'Em trânsito',
    OUT_FOR_DELIVERY: 'Saiu para entrega',
    DELIVERY_FAILURE: 'Falha na entrega',
    RESCHEDULED: 'Reagendado',
    DELIVERED: 'Entregue',
    RETURNED: 'Retornou ao remetente',
    CANCELLED: 'Cancelado',
};

/**
 * Formata um objeto de endereço da API para string (ex.: "Rua X, 123, Campinas - SP").
 * Entregas em criação podem ter gatheringAddress/deliveryAddress vazios.
 */
function formatAddressFromApi(addr) {
    if (!addr || typeof addr !== 'object') return '—';
    const alias = normalizeAddressName(addr.name);
    const parts = [
        alias,
        [addr.street, addr.number, addr.complement].filter(Boolean).join(', '),
        [addr.district, addr.city, addr.state].filter(Boolean).join(' — '),
        addr.zipCode,
    ].filter(Boolean);
    return parts.length ? parts.join(', ') : '—';
}

/**
 * Mapeia um item do retorno da API para o formato usado pela listagem.
 * @param {Object} item - { id, customerName, customerType, customerId, state, deliveryHeaderDetail }
 */
export function mapDeliveryHeader(item = {}) {
    const state = item.state || {};
    const normalizedStatus = normalizeDeliveryStateFromApi(state.value || '');
    const detail = item.deliveryHeaderDetail || {};
    const gathering = detail.gatheringAddress || {};
    const delivery = detail.deliveryAddress || {};
    return {
        id: item.id,
        customerName: displayCustomerName(item.customerName),
        customerType: item.customerType ?? null,
        status: normalizedStatus,
        statusLabel: state.status || STATUS_LABELS[normalizedStatus] || STATUS_LABELS[state.value] || state.value || '—',
        expectedDate: item.scheduledDeliveryDateTime || null,
        value: item.deliveryBillTotalAmount ?? null,
        viewed: false,
        pickupAddress: formatAddressFromApi(gathering),
        deliveryAddress: formatAddressFromApi(delivery),
        recipientName: detail.responsibleReceiptName || '—',
        customerId: item.customerId ?? null,
        delayed: item.delayed === true,
    };
}

/** Campos de ordenação aceitos pela UI → parâmetro sort da API. */
const SORT_FIELD_MAP = {
    id: 'id',
    state: 'state',
    scheduledDeliveryDateTime: 'scheduledDeliveryDateTime',
    expectedDate: 'scheduledDeliveryDateTime',
    value: 'bill.totalAmount',
    deliveryBillTotalAmount: 'bill.totalAmount',
};

function assertDeliveryDateRange(startDate, endDate) {
    if (startDate && endDate) {
        const validation = validateDeliveryDateRange(startDate, endDate);
        if (!validation.valid) {
            throw new Error(validation.message);
        }
    }
}

/**
 * Filtros compartilhados por GET /api/entregas e GET /api/entregas/status.
 * @param {URLSearchParams} params
 * @param {Object} filters
 */
function appendDeliveryListFilters(params, {
    customerId,
    id,
    state,
    status,
    startDate,
    endDate,
    delayed,
} = {}) {
    if (customerId != null && String(customerId).trim() !== '') {
        params.set('customerId', String(customerId));
    }
    if (id != null && String(id).trim() !== '') {
        params.set('id', String(id).trim());
    }
    toApiDeliveryStates(state ?? status).forEach((stateValue) => {
        params.append('state', stateValue);
    });
    if (startDate) {
        params.set('startDate', startDate);
    }
    if (endDate) {
        params.set('endDate', endDate);
    }
    if (delayed === true || delayed === 'true') {
        params.set('delayed', 'true');
    }
    return params;
}

/**
 * Monta query string compartilhada pelas listagens de entregas.
 * @param {Object} params
 * @returns {URLSearchParams}
 */
export function buildDeliveryListParams({
    page = 0,
    size = 20,
    sort = 'id,desc',
    ...filters
} = {}) {
    assertDeliveryDateRange(filters.startDate, filters.endDate);

    const [field, direction] = (sort || 'id,desc').split(',');
    const apiField = SORT_FIELD_MAP[field?.trim()] || field?.trim() || 'id';
    const apiSort = `${apiField},${(direction || 'desc').toLowerCase()}`;
    const cappedSize = Math.min(Math.max(1, Number(size) || 20), MAX_DELIVERY_PAGE_SIZE);

    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('size', String(cappedSize));
    params.set('sort', apiSort);
    return appendDeliveryListFilters(params, filters);
}

/**
 * Monta query string de GET /api/entregas/status.
 * Usa os mesmos filtros da listagem, sem status, paginação nem ordenação.
 * @param {Object} params
 * @returns {URLSearchParams}
 */
export function buildDeliveryStatusCountParams(filters = {}) {
    assertDeliveryDateRange(filters.startDate, filters.endDate);
    const sharedFilters = { ...filters };
    delete sharedFilters.state;
    delete sharedFilters.status;
    return appendDeliveryListFilters(new URLSearchParams(), sharedFilters);
}

/**
 * Mapeia um item de GET /entregas/status para o enum usado na UI.
 * @param {{ status?: string, count?: number }} item
 */
export function mapDeliveryStatusCountItem(item = {}) {
    return {
        status: normalizeDeliveryStateFromApi(item.status),
        count: Number(item.count) || 0,
    };
}

/**
 * Lista a distribuição de status das entregas (GET /api/entregas/status).
 * Aplica os mesmos filtros de {@link list}, sem status, page, size nem sort.
 * @param {Object} params - Mesmos filtros de {@link list}
 * @returns {Promise<Array<{ status: string, count: number }>>}
 */
async function listStatusCounts(params = {}) {
    const searchParams = buildDeliveryStatusCountParams(params);
    const qs = searchParams.toString();
    const path = qs ? `/api/entregas/status?${qs}` : '/api/entregas/status';
    const response = await httpClient.get(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    if (!Array.isArray(json)) {
        return [];
    }
    return json.map(mapDeliveryStatusCountItem);
}

/**
 * Executa listagem paginada de headers de entrega.
 * @param {string} path - Endpoint relativo (ex.: /api/entregas)
 * @param {Object} listParams
 */
async function fetchDeliveryHeaders(path, listParams = {}) {
    const params = buildDeliveryListParams(listParams);
    const response = await httpClient.get(`${path}?${params.toString()}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }

    const json = await response.json();
    const content = (json.content || []).map(mapDeliveryHeader);
    const pageMeta = json.page || {};
    const totalPages = pageMeta.totalPages ?? 0;
    const pageNumber = Math.max(0, pageMeta.number ?? 0);

    return {
        content,
        page: {
            number: pageNumber,
            totalPages,
            first: totalPages === 0 || pageNumber === 0,
            last: totalPages === 0 || pageNumber >= totalPages - 1,
        },
    };
}

/**
 * Lista entregas (GET /api/entregas).
 * @param {Object} params
 * @param {number} params.page - Página (0-based)
 * @param {number} params.size - Tamanho da página (máx. 30)
 * @param {string} params.sort - Ordenação (ex: "id,desc")
 * @param {string|null|undefined} [params.customerId] - Filtrar por cliente
 * @param {string|number|null|undefined} [params.id] - Identificador exato da entrega
 * @param {string|string[]|null|undefined} [params.state] - Estado(s) da entrega (enum API)
 * @param {string|string[]|null|undefined} [params.status] - Alias de state (enum da UI)
 * @param {string|null|undefined} [params.startDate] - Início do intervalo de criação (yyyy-MM-dd)
 * @param {string|null|undefined} [params.endDate] - Fim do intervalo de criação (yyyy-MM-dd)
 * @param {boolean|string|null|undefined} [params.delayed] - Quando true, somente entregas atrasadas
 */
async function list(params = {}) {
    return fetchDeliveryHeaders('/api/entregas', params);
}

/**
 * Lista entregas elegíveis para cobrança (GET /api/entregas/cobraveis).
 * Mesmos filtros da listagem geral; o backend restringe a cobranças faturáveis e ainda não vinculadas a fatura.
 * @param {Object} params - Mesmos parâmetros de {@link list}
 */
async function listBillable(params = {}) {
    return fetchDeliveryHeaders('/api/entregas/cobraveis', params);
}

/**
 * Normaliza dateTime para o formato aceito por java.time.LocalDateTime (ISO-8601 local).
 * Formato de saída: yyyy-MM-dd'T'HH:mm:ss (sem timezone).
 * Aceita: yyyy-MM-dd, yyyy-MM-ddTHH:mm ou yyyy-MM-ddTHH:mm:ss (completa com :00 quando faltar).
 */
function toLocalDateTimeString(value) {
    if (value == null || typeof value !== 'string') return null;
    const s = value.trim();
    if (!s) return null;
    const withoutTz = s.replace(/Z$/i, '').replace(/[+-]\d{2}:?\d{2}$/, '').trim();
    if (withoutTz.length < 10) return null;
    const dateOnly = /^(\d{4}-\d{2}-\d{2})$/;
    const dateTimeMatch = withoutTz.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?(?:\.\d+)?$/);
    if (dateOnly.test(withoutTz)) {
        return `${withoutTz}T00:00:00`;
    }
    if (dateTimeMatch) {
        const [, date, h, m, sec] = dateTimeMatch;
        const hour = h.padStart(2, '0');
        const min = m.padStart(2, '0');
        const second = (sec !== undefined && sec !== '' ? sec : '00').padStart(2, '0');
        return `${date}T${hour}:${min}:${second}`;
    }
    if (withoutTz.length >= 19) return withoutTz.slice(0, 19);
    return null;
}

/**
 * Converte telefone BR "(11) 99999-9999" em { areaCode, number } para API.
 */
function phoneToDto(phoneStr) {
    if (phoneStr == null || typeof phoneStr !== 'string') return null;
    const digits = phoneStr.replace(/\D/g, '');
    if (digits.length < 10) return null;
    return {
        areaCode: digits.length === 11 ? digits.slice(0, 2) : digits.slice(0, 2),
        number: digits.length === 11 ? digits.slice(2) : digits.slice(2),
    };
}

/**
 * Cria um item da entrega.
 * POST /api/entregas/{deliveryId}/itens
 * @param {string|number} deliveryId
 * @param {Object} item - { quantity, height, width, length, weight, fragile, price, description, documentNumber }
 * @param {string|number|null} [customerId]
 * @returns {Promise<{ id: number|null }>}
 */
async function createItem(deliveryId, item, customerId) {
    const body = buildItemBody(item);
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/itens`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.post(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const idFromLocation = location ? location.replace(/.*\//, '').replace(/\?.*$/, '').trim() : null;
    return { id: idFromLocation != null ? Number(idFromLocation) : null };
}

/**
 * Monta o body de item para create/update (quantity, dimensions, fragile, price, description, externalId).
 */
function buildItemBody(item) {
    const quantity = parseInt(item?.quantity, 10);
    if (!Number.isInteger(quantity) || quantity < 1) {
        throw new Error('Quantidade é obrigatória e deve ser maior ou igual a 1.');
    }
    const description = (item?.description || '').trim();
    if (!description) {
        throw new Error('Descrição é obrigatória.');
    }
    return {
        quantity,
        height: item?.height != null && item.height !== '' ? Number(item.height) : null,
        width: item?.width != null && item.width !== '' ? Number(item.width) : null,
        length: item?.length != null && item.length !== '' ? Number(item.length) : null,
        weight: item?.weight != null && item.weight !== '' ? Number(item.weight) : null,
        fragile: !!item?.fragile,
        price: item?.price != null && item.price !== '' ? Number(item.price) : null,
        description,
        externalId: item?.documentNumber ? String(item.documentNumber).trim() || null : null,
    };
}

/**
 * Atualiza um item da entrega.
 * PUT /api/entregas/{deliveryId}/itens/{itemId}
 * @param {string|number} deliveryId
 * @param {string|number} itemId - ID do item
 * @param {Object} item - mesmos campos de createItem
 * @param {string|number|null} [customerId] - obrigatório para empresa de transporte
 */
async function updateItem(deliveryId, itemId, item, customerId) {
    const body = buildItemBody(item);
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/itens/${encodeURIComponent(itemId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.put(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Remove um item da entrega.
 * DELETE /api/entregas/{deliveryId}/itens/{itemId}
 * @param {string|number} deliveryId
 * @param {string|number} itemId - ID do item retornado ao criar (Location)
 * @param {string|number|null} [customerId] - obrigatório para empresa de transporte
 */
async function deleteItem(deliveryId, itemId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/itens/${encodeURIComponent(itemId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.delete(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Atualiza endereço de coleta ou de entrega da entrega.
 * PATCH /api/entregas/{deliveryId}/endereco
 * Payload: UpdateDeliveryAddressRequest (name, street, number, district, city, zipCode, complement, state, target, dateTime, responsible, instructions)
 * @param {string|number} deliveryId - ID da entrega
 * @param {Object} address - { name?, street, number, district, city, zipCode, complement, stateAbbr, stateName? }
 * @param {'GATHERING'|'DELIVERY'} target - GATHERING = coleta, DELIVERY = entrega
 * @param {Object} [options]
 * @param {string|number|null} [options.customerId]
 * @param {string|null} [options.dateTime] - ISO-8601 local date-time para java.time.LocalDateTime (yyyy-MM-dd'T'HH:mm:ss)
 * @param {string|null} [options.instructions]
 * @param {Object|null} [options.responsible] - { id } para existente ou { name, document, phoneNumber } para novo (apenas DELIVERY)
 */
async function updateAddress(deliveryId, address, target, options = {}) {
    const customerId = options.customerId ?? (typeof options === 'string' || typeof options === 'number' ? options : undefined);
    const dateTime = options.dateTime ?? null;
    const instructions = options.instructions ?? null;
    const responsible = options.responsible ?? null;

    const zipCode = (address?.zipCode ?? '').toString().replace(/\D/g, '');
    if (!zipCode) {
        throw new Error('CEP é obrigatório');
    }
    const body = {
        name: normalizeAddressName(address?.name),
        street: address?.street ?? '',
        number: address?.number ?? '',
        district: address?.district ?? '',
        city: address?.city ?? '',
        zipCode,
        complement: address?.complement ?? '',
        state: {
            abbreviation: (address?.stateAbbr ?? '').trim() || null,
            name: (address?.stateName ?? address?.stateAbbr ?? '').trim() || null,
        },
        target,
        dateTime: toLocalDateTimeString(dateTime),
        instructions: instructions != null && String(instructions).trim() !== '' ? String(instructions).trim() : null,
        responsible: null,
    };
    if (responsible && target === 'DELIVERY') {
        if (responsible.id != null) {
            body.responsible = { id: Number(responsible.id) };
        } else if (responsible.name != null && String(responsible.name).trim() !== '') {
            body.responsible = {
                name: String(responsible.name).trim(),
                document: responsible.document ? String(responsible.document).replace(/\D/g, '') : null,
                phoneNumber: phoneToDto(responsible.phoneNumber ?? responsible.phone ?? ''),
            };
        }
    }
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/endereco`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.patch(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Cria ou atualiza a cobrança principal da entrega (valor + descrição).
 * PATCH /api/entregas/{deliveryId}/cobranca
 * @param {string|number} deliveryId
 * @param {{ amount: number|null, description?: string|null }} payload - amount já em reais (ex.: 520 para R$ 520,00)
 * @param {string|number|null} [customerId] - obrigatório para empresa de transporte quando cliente selecionado
 * @returns {Promise<{ id: number|null }>} id da cobrança do header Location (se presente)
 */
async function updateDeliveryBill(deliveryId, payload, customerId) {
    const amount = Number(payload?.amount) || 0;
    const body = {
        amount,
        description: payload?.description != null && String(payload.description).trim() !== '' ? String(payload.description).trim() : null,
    };
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/cobranca`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.patch(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const idFromLocation = location ? location.replace(/.*\//, '').replace(/\?.*$/, '').trim() : null;
    return { id: idFromLocation != null ? Number(idFromLocation) : null };
}

/**
 * Cria uma taxa da cobrança da entrega.
 * POST /api/entregas/{deliveryId}/cobranca/taxas
 * @param {string|number} deliveryId
 * @param {{ calculationBasis: number, amount: number, description: string, percentage: boolean }} payload
 * @param {string|number|null} [customerId]
 * @returns {Promise<{ id: number|null }>} id da taxa do header Location (se presente)
 */
async function createFee(deliveryId, payload, customerId) {
    const body = {
        calculationBasis: Number(payload.calculationBasis) ?? 0,
        amount: Number(payload.amount) ?? 0,
        description: payload.description != null && String(payload.description).trim() !== '' ? String(payload.description).trim() : 'Taxa',
        percentage: Boolean(payload.percentage),
    };
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/cobranca/taxas`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.post(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const location = response.headers.get('Location');
    const idFromLocation = location ? location.replace(/.*\//, '').replace(/\?.*$/, '').trim() : null;
    return { id: idFromLocation != null ? Number(idFromLocation) : null };
}

/**
 * Atualiza uma taxa da cobrança da entrega.
 * PATCH /api/entregas/{deliveryId}/cobranca/taxas/{feeId}
 * @param {string|number} deliveryId
 * @param {string|number} feeId
 * @param {{ calculationBasis: number, amount: number, description: string, percentage: boolean }} payload
 * @param {string|number|null} [customerId]
 * @returns {Promise<void>}
 */
async function updateFee(deliveryId, feeId, payload, customerId) {
    const body = {
        calculationBasis: Number(payload.calculationBasis) ?? 0,
        amount: Number(payload.amount) ?? 0,
        description: payload.description != null && String(payload.description).trim() !== '' ? String(payload.description).trim() : 'Taxa',
        percentage: Boolean(payload.percentage),
    };
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/cobranca/taxas/${encodeURIComponent(feeId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.patch(path, body);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Remove uma taxa da cobrança da entrega.
 * DELETE /api/entregas/{deliveryId}/cobranca/taxas/{feeId}
 * @param {string|number} deliveryId
 * @param {string|number} feeId
 * @param {string|number|null} [customerId]
 * @returns {Promise<void>}
 */
async function deleteFee(deliveryId, feeId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/cobranca/taxas/${encodeURIComponent(feeId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.delete(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Atualiza o solicitante da entrega.
 * PATCH /api/entregas/{deliveryId}/solicitante
 * Transportadora envia `customerId` na query. Pessoa jurídica omite — o backend usa o cliente do token.
 * Resposta de sucesso: 204 sem corpo.
 *
 * @param {string|number} deliveryId
 * @param {string|number} requesterId - id do funcionário (employee), não do usuário
 * @param {string|number|null|undefined} customerId - obrigatório para empresa de transporte
 * @param {boolean} isTransportCompany
 * @returns {Promise<void>}
 */
async function updateDeliveryRequester(deliveryId, requesterId, customerId, isTransportCompany) {
    if (requesterId == null || String(requesterId).trim() === '') {
        throw new Error('Informe o solicitante.');
    }
    if (isTransportCompany && (customerId == null || String(customerId).trim() === '')) {
        throw new Error('Customer ID is required!');
    }
    const params = new URLSearchParams();
    if (isTransportCompany) {
        params.set('customerId', String(customerId));
    }
    const qs = params.toString();
    const path = `/api/entregas/${encodeURIComponent(deliveryId)}/solicitante${qs ? `?${qs}` : ''}`;
    const response = await httpClient.patch(path, { requesterId: Number(requesterId) });
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Consulta o centro de custo atribuído à entrega.
 * GET /api/entregas/{deliveryId}/centro-de-custo
 *
 * @param {string|number} deliveryId
 * @param {string|number|null|undefined} customerId - obrigatório para empresa de transporte quando cliente selecionado
 * @returns {Promise<{ id: number|null, active: boolean, name: string, code: string }>}
 */
async function getDeliveryCostCenter(deliveryId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/centro-de-custo`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.get(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    // Alguns cenários podem retornar 200 com body vazio (ou 204) quando não há centro de custo.
    // Evita "Unexpected end of JSON input".
    const text = await response.text();
    const trimmed = String(text || '').trim();
    if (!trimmed) return null;
    try {
        return JSON.parse(trimmed);
    } catch {
        return null;
    }
}

/**
 * Atualiza o centro de custo atribuído à entrega.
 * PATCH /api/entregas/{deliveryId}/centro-de-custo?costCenterId=...&customerId=...
 * Para remover a atribuição, não envie `costCenterId` (somente `customerId` quando aplicável).
 *
 * @param {string|number} deliveryId
 * @param {string|number|null|undefined} costCenterId - omitido na URL quando `null`/`undefined` (remoção)
 * @param {string|number|null|undefined} customerId - opcional (mas obrigatório para empresa de transporte)
 * @returns {Promise<void>}
 */
async function updateDeliveryCostCenter(deliveryId, costCenterId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/centro-de-custo`;
    const params = new URLSearchParams();
    if (costCenterId != null && String(costCenterId).trim() !== '') {
        params.set('costCenterId', String(costCenterId));
    }
    if (customerId != null && String(customerId).trim() !== '') {
        params.set('customerId', String(customerId));
    }
    const qs = params.toString();
    if (qs) {
        path += `?${qs}`;
    }
    const response = await httpClient.patch(path, null);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Status permitidos a partir do estado atual (empresa de transporte).
 * GET /dados/status-de-entrega-possiveis?from=...
 * @param {string} fromState - valor do enum (ex.: IN_TRANSIT)
 * @returns {Promise<string[]>}
 */
async function getPossibleDeliveryStatuses(fromState) {
    const params = new URLSearchParams();
    params.set('from', String(fromState));
    const response = await httpClient.get(`/dados/status-de-entrega-possiveis?${params.toString()}`);
    if (!response.ok) {
        throw await parseApiError(response);
    }
    const json = await response.json();
    const raw = json?.deliveryStatuses;
    if (Array.isArray(raw)) {
        return raw.map((s) => (typeof s === 'string' ? s : s?.value ?? String(s)));
    }
    return [];
}

/**
 * Atualiza o status da entrega.
 * PATCH /api/entregas/{deliveryId}/status?state=...&customerId=...&images=id1&images=id2
 * @param {string|number} deliveryId
 * @param {string} state - enum (ex.: DELIVERED)
 * @param {string|number} customerId
 * @param {number[]} [imageIds] - IDs das imagens previamente enviadas ao bucket
 * @returns {Promise<void>}
 */
async function updateDeliveryStatus(deliveryId, state, customerId, imageIds) {
    const params = new URLSearchParams();
    params.set('state', String(state));
    params.set('customerId', String(customerId));
    if (Array.isArray(imageIds) && imageIds.length > 0) {
        imageIds.forEach((id) => params.append('images', String(id)));
    }
    const path = `/api/entregas/${encodeURIComponent(deliveryId)}/status?${params.toString()}`;
    const response = await httpClient.patch(path, null);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

/**
 * Monta o endpoint para solicitar URLs pré-assinadas de upload de imagens de uma entrega.
 * As extensões (requiredExtensions) são acrescentadas pelo imageUploadClient conforme os arquivos selecionados.
 * @param {string|number} deliveryId
 * @param {string|number} customerId
 * @returns {string} Endpoint sem requiredExtensions
 */
function buildDeliveryImagesEndpoint(deliveryId, customerId) {
    return `/api/entregas/${encodeURIComponent(deliveryId)}/imagens?customerId=${encodeURIComponent(String(customerId))}`;
}

/**
 * Remove uma foto já vinculada à entrega.
 * DELETE /api/entregas/{deliveryId}/imagens/{photoId}?customerId=...
 * @param {string|number} deliveryId
 * @param {string|number} photoId
 * @param {string|number} customerId
 * @returns {Promise<void>}
 */
/**
 * Indica se a entrega possui roteiros com pontos associados (campo `routes` do GET detalhado).
 * @param {Array<{ id?: number, points?: number[] }>|null|undefined} routes
 * @returns {boolean}
 */
function hasDeliveryRoutePoints(routes) {
    if (!Array.isArray(routes) || routes.length === 0) return false;
    return routes.some((route) => Array.isArray(route?.points) && route.points.length > 0);
}

async function deleteDeliveryImage(deliveryId, photoId, customerId) {
    let path = `/api/entregas/${encodeURIComponent(deliveryId)}/imagens/${encodeURIComponent(photoId)}`;
    if (customerId != null && String(customerId).trim() !== '') {
        path += `?customerId=${encodeURIComponent(String(customerId))}`;
    }
    const response = await httpClient.delete(path);
    if (!response.ok) {
        throw await parseApiError(response);
    }
}

const deliveryService = {
    list,
    listBillable,
    listStatusCounts,
    createDelivery,
    getDeliveryById,
    cancelDelivery,
    requestDelivery,
    createItem,
    updateItem,
    deleteItem,
    updateAddress,
    updateDeliveryBill,
    createFee,
    updateFee,
    deleteFee,
    updateDeliveryRequester,
    getDeliveryCostCenter,
    updateDeliveryCostCenter,
    getPossibleDeliveryStatuses,
    updateDeliveryStatus,
    buildDeliveryImagesEndpoint,
    deleteDeliveryImage,
    hasDeliveryRoutePoints,
    STATUS_LABELS,
};

export default deliveryService;
